// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import type { SimplePageContent } from "@tmi/types/entities";
import LocalForagePlugin, { simplePages, syncState } from "@/plugins/LocalForagePlugin";

import { refreshSimplePagesFromServer } from "@/services/data/sync/simple-page-sync-coordinator";
import { NotFound } from "@tmi/types/api/responses";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

// State values
const initialized = computed< boolean >( () => allSimplePages.value.length > 0 );
const loading = ref< boolean >( false );

const allSimplePagesBySlugMap = new Map< string, SimplePageContent >();
const defaultSimplePages: SimplePageContent[] = [];
const allSimplePages = ref< SimplePageContent[] >( defaultSimplePages );

export const useSimplePagesStore = defineStore( 'simple-pages', () => {
    function reset(): void {
        allSimplePages.value.splice( 0 );
        allSimplePagesBySlugMap.clear();

        loading.value = false;
    }

    async function initializeAsync(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Simple-Pages ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Simple-Pages ] Checking the local database for any simple page content...` );

            loading.value = true;

            const t0: number = performance.now();

            if ( typeof simplePages === 'undefined' || typeof syncState === 'undefined' ) {
                // Wait for the driver to settle
                LocalForagePlugin.initialize();
            }

            await simplePages.ready();
            const numberSimplePages = await simplePages.length();

            const uniquePages: Map< string, SimplePageContent > = new Map< string, SimplePageContent >();
            if ( numberSimplePages > 0 ) {
                // Clear the contents of the searchable collections
                allSimplePages.value.splice( 0 );

                // Iterate over all elements within IDB
                await simplePages.iterate( ( page: SimplePageContent ) => {
                    uniquePages.set( page.id, page );
                    return;
                } );

                allSimplePages.value = [ ...( Array.from( uniquePages.values() ) ) ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allSimplePages.value.length } simple-pages in ${ t1 - t0 } ms` );
            } else {
                await fetchFromServer();
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    async function fetchFromServer() {
        try {
            if ( loading.value ) {
                return;
            }

            loading.value = true;

            const receivedSimplePages: SimplePageContent[] = await refreshSimplePagesFromServer();
            for ( const sp of receivedSimplePages ) {
                allSimplePagesBySlugMap.set( sp.slug, sp );
            }

            allSimplePages.value = [ ...( Array.from( allSimplePagesBySlugMap.values() ) ) ];
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    async function fetchSimplePageBySlugIfMissing( slug: string ): Promise< SimplePageContent | undefined > {
        // Best case scenario is that the simple page has already been loaded and is immediately available
        let requestedSimplePage: SimplePageContent | undefined = allSimplePagesBySlugMap.get( slug );
        if ( requestedSimplePage ) {
            return requestedSimplePage;
        }

        // clear out whatever is in memory
        allSimplePages.value = defaultSimplePages;
        allSimplePagesBySlugMap.clear();

        // retrieve the current simple page catalog
        await fetchFromServer();

        // After reloading the simple page catalog, re-test for the requested slug
        requestedSimplePage = allSimplePagesBySlugMap.get( slug );
        if ( !requestedSimplePage ) {
            throw new NotFound();
        }

        return requestedSimplePage;
    }

    return {
        // ACTIONS
        fetchSimplePageBySlugIfMissing,
        initializeAsync,
        reset,

        // GETTERS
        allSimplePages
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSimplePagesStore, import.meta.hot ) );
}
